// src/context/CheckoutContext.js
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import * as Sentry from '@sentry/react';
import { useCart } from './CartContext';
import { useCloverContext } from 'context/CloverContext';
import { useModal } from 'context/ModalContext';
import { useToast } from 'context/ToastContext';
import { validateCustomerInfo } from 'utils/checkoutValidation';
import { validateCart } from 'utils/cartValidation';
import { generateRequestId } from 'utils/checkoutHelpers';
import {
  buildPayOrderError,
  describeNetworkFailure,
  readErrorBody,
} from 'utils/paymentErrors';
import {
  saveCheckoutState,
  loadCheckoutState,
  clearCheckoutState,
  saveCustomerInfo,
  loadCustomerInfo,
  buildReceiptSnapshot,
  saveReceipt,
  loadReceipt,
  clearReceipt,
} from 'utils/checkoutStateUtils';
import { deleteOrder } from 'services/checkoutAPI';

const CheckoutContext = createContext();

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
};

export const CheckoutProvider = ({ children }) => {
  const { getCloverOrderData, cartItems, clearCart, removeCartItem } =
    useCart();
  const { menuData } = useCloverContext();
  const { openModal } = useModal();
  const { showError, showWarning } = useToast();

  // Load persisted state on initialization
  const persistedState = loadCheckoutState();
  const persistedCustomerInfo = loadCustomerInfo(); // Load customer info separately

  // A finished order's receipt outlives the checkout state (#584). It is
  // restored only when nothing newer is in progress and the cart is still
  // empty — a customer who has put something new in the cart is starting the
  // next order, not looking for the last one.
  const persistedReceipt =
    persistedState || cartItems.length > 0 ? null : loadReceipt();
  const restoredReceiptRef = useRef(Boolean(persistedReceipt));
  // Orders paid, and the one being paid right now. Refs, not state, so two
  // calls in the same tick both see them. Clover's wallet SDK delivers each
  // payment event several times, sometimes with a second token (#612).
  const paidOrderIdsRef = useRef(new Set());
  const payingOrderIdRef = useRef(null);

  // Customer info loaded from localStorage

  // State management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResponse, setOrderResponse] = useState(
    persistedState?.orderResponse || persistedReceipt?.orderResponse || null,
  );
  const [error, setError] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState(
    persistedState?.paymentResponse ||
      persistedReceipt?.paymentResponse ||
      null,
  );
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Customer info state - restore from separate localStorage (persists across orders)
  const [customerInfo, setCustomerInfo] = useState(
    persistedCustomerInfo || {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
    },
  );

  // New checkout fields - restore from persisted state if available
  // null until the customer chooses ASAP or a slot (#484). A persisted choice
  // from an earlier visit is kept so a returning customer is not asked twice.
  const [pickupTime, setPickupTime] = useState(
    persistedState?.pickupTime ?? persistedReceipt?.pickupTime ?? null,
  );
  const [tipAmount, setTipAmount] = useState(
    persistedState?.tipAmount || persistedReceipt?.tipAmount || 0,
  ); // in cents
  // Tip percentage is restored if we have tip data (either active order or preserved from going back)
  // This allows tip recalculation after cart changes and preserves selection when editing
  const [tipPercentage, setTipPercentage] = useState(
    persistedState?.tipPercentage || null,
  ); // Store the percentage (15, 18, 20) or null for custom/none
  const [orderNotes, setOrderNotes] = useState(
    persistedState?.orderNotes || persistedReceipt?.orderNotes || '',
  );

  // Something in the cart means the next order is underway, so the last
  // receipt is done with. The cart loads from localStorage after the first
  // render, so a RESTORED receipt can find this out a beat late and is
  // dropped from state too. The live confirmation is never touched: it
  // clears the cart itself, and while paymentResponse is set nothing here
  // runs against it.
  useEffect(() => {
    if (cartItems.length === 0) return;
    if (restoredReceiptRef.current) {
      restoredReceiptRef.current = false;
      setPaymentResponse(null);
      setOrderResponse(null);
      setPickupTime(null);
      setTipAmount(0);
      setOrderNotes('');
      clearReceipt();
    } else if (!paymentResponse) {
      clearReceipt();
    }
  }, [cartItems, paymentResponse]);

  // Cart change detection
  const [cartSnapshot, setCartSnapshot] = useState(
    persistedState?.cartSnapshot || null,
  ); // Snapshot of cart when order was created
  const [hasCartChanged, setHasCartChanged] = useState(false); // Flag for cart changes

  // Persist customer info separately (persists across orders)
  // NOTE: We only save if there's actual data, to avoid overwriting with empty values
  useEffect(() => {
    const hasCustomerInfo =
      customerInfo.firstName ||
      customerInfo.lastName ||
      customerInfo.email ||
      customerInfo.phone;

    // Only save if we have customer info
    // This prevents overwriting saved data when state is cleared
    if (hasCustomerInfo) {
      saveCustomerInfo(customerInfo);
    }
  }, [customerInfo]);

  // Persist checkout state to localStorage whenever it changes
  useEffect(() => {
    // Don't persist state if payment is complete (user is on confirmation screen)
    if (paymentResponse) {
      return;
    }

    // Check if we have any meaningful state to persist
    const hasCustomerInfo =
      customerInfo.firstName ||
      customerInfo.lastName ||
      customerInfo.email ||
      customerInfo.phone;
    const hasOrderData = orderResponse?.order?.id;

    // Only persist if we have meaningful state (any customer info or order created)
    if (hasCustomerInfo || hasOrderData) {
      // Saving checkout state to localStorage
      saveCheckoutState({
        customerInfo,
        pickupTime,
        tipAmount,
        tipPercentage, // Save tip percentage to enable recalculation after cart changes
        orderNotes,
        orderResponse,
        paymentResponse,
        cartSnapshot, // Save cart snapshot for change detection after page refresh
      });
    }
  }, [
    customerInfo,
    pickupTime,
    tipAmount,
    tipPercentage,
    orderNotes,
    orderResponse,
    paymentResponse,
    cartSnapshot,
  ]);

  // Detect cart changes after order creation
  useEffect(() => {
    // Only check if we have an order and a snapshot to compare against
    if (!orderResponse?.order?.id || !cartSnapshot) {
      return;
    }

    // Don't check if payment is already complete
    if (paymentResponse) {
      return;
    }

    // Compare current cart with snapshot
    const cartChanged =
      JSON.stringify(cartItems) !== JSON.stringify(cartSnapshot);

    if (cartChanged) {
      setHasCartChanged(true);
    } else {
      setHasCartChanged(false);
    }
  }, [cartItems, cartSnapshot, orderResponse, paymentResponse]);

  // Recalculate tip amount when cart changes and user had selected a percentage
  useEffect(() => {
    // Only recalculate if:
    // 1. User had selected a percentage tip (not custom or none)
    // 2. Cart has changed
    // 3. We have cart items to calculate from
    if (tipPercentage && hasCartChanged && cartItems.length > 0) {
      // Calculate new subtotal from cart
      const newSubtotal = cartItems.reduce((sum, item) => {
        return sum + (item.subtotal || item.price * item.quantity);
      }, 0);

      // Recalculate tip based on percentage
      const newTipAmount = Math.round((newSubtotal * tipPercentage) / 100);

      // Only update if the amount actually changed
      if (newTipAmount !== tipAmount) {
        setTipAmount(newTipAmount);

        Sentry.addBreadcrumb({
          message: 'Tip amount recalculated due to cart change',
          category: 'checkout',
          level: 'info',
          data: {
            tipPercentage,
            oldTipAmount: tipAmount,
            newTipAmount,
            newSubtotal,
          },
        });
      }
    }
  }, [cartItems, hasCartChanged, tipPercentage, tipAmount]);

  // Customer info handlers
  const updateCustomerInfo = useCallback(
    (field, value) => {
      setCustomerInfo((prev) => ({ ...prev, [field]: value }));

      // Clear validation error for this field
      if (validationErrors[field]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [validationErrors],
  );

  const validateAndSubmitOrder = useCallback(async () => {
    // The pickup question has no default (#484). The Continue buttons are
    // disabled without an answer; this is the backstop for a cleared state.
    if (!pickupTime) {
      setError('Please choose ASAP or a pickup time.');
      return;
    }

    // Validate customer info using imported utility
    const errors = validateCustomerInfo(customerInfo);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError('Please fill in all required fields correctly.');

      // Validation failed
      return;
    }

    // Proceed with order submission
    if (cartItems.length === 0) {
      setError('Your cart is empty. Please add items before checking out.');
      return;
    }

    // Validate cart items against live menu data
    const validation = validateCart(cartItems, menuData?.categories || []);

    if (!validation.valid) {
      // Case 2: item(s) deleted from Clover — auto-remove and toast
      if (validation.invalidItems.length > 0) {
        for (const invalidItem of validation.invalidItems) {
          removeCartItem(invalidItem.cartItemId);
        }
        const itemNames = validation.invalidItems
          .map((i) => i.baseItem?.name || 'item')
          .join(', ');
        showError(
          `${itemNames} ${validation.invalidItems.length === 1 ? 'is' : 'are'} no longer available and ${validation.invalidItems.length === 1 ? 'has' : 'have'} been removed from your cart. Please review your order.`,
        );
        return;
      }

      // Case 1: modifier(s) stale — open modal for the first affected item
      if (validation.invalidModifiers.length > 0) {
        const { cartItem, staleModifierIds, freshItem } =
          validation.invalidModifiers[0];
        showWarning(
          `One of your selections for "${freshItem.name || cartItem.baseItem?.name}" is no longer available. Please choose a new option.`,
        );
        // Convert flat modifiers array to groupId-keyed object for ModalContext,
        // pre-selecting still-valid modifiers and omitting the stale ones.
        const staleSet = new Set(staleModifierIds);
        const selectedModifiers = {};
        for (const group of freshItem.modifierGroups || []) {
          for (const mod of group.modifiers || []) {
            if (
              !staleSet.has(mod.id) &&
              (cartItem.modifiers || []).some((m) => m.id === mod.id)
            ) {
              selectedModifiers[group.id] = mod.id;
            }
          }
        }
        openModal(freshItem, { ...cartItem, modifiers: selectedModifiers });
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);
    setOrderResponse(null);
    setValidationErrors({});

    try {
      // Add customer info to order data
      const orderData = getCloverOrderData();
      const orderWithCustomer = {
        ...orderData,
        customerInfo,
      };

      const requestBody = {
        orderCart: orderWithCustomer.orderCart,
        customerInfo,
        pickupTime,
        tipAmount,
        orderNotes,
      };

      // Log request for debugging
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[CheckoutContext] Creating order with data:', {
          lineItemCount: requestBody.orderCart?.lineItems?.length,
          customerInfo: requestBody.customerInfo,
          pickupTime: requestBody.pickupTime,
          tipAmount: requestBody.tipAmount,
          hasNotes: !!requestBody.orderNotes,
        });
      }

      const response = await fetch('/.netlify/functions/createOrder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': generateRequestId(), // Add request tracking
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await readErrorBody(response, 'createOrder');

        // Log detailed error for debugging
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.error('[CheckoutContext] Order creation failed:', {
            status: response.status,
            errorData,
            orderData: {
              customerInfo,
              pickupTime,
              tipAmount,
              hasNotes: !!orderNotes,
            },
          });
        }

        const orderError = new Error(
          errorData.error ||
            `Server error (${response.status}): Failed to create order`,
        );
        orderError.cloverCode = errorData.details?.message || null;
        orderError.code = errorData.code || null;
        throw orderError;
      }

      const result = await response.json();

      if (!result.order?.id) {
        // eslint-disable-next-line no-console
        console.error(
          '[CheckoutContext] Invalid order response - missing order ID',
        );
        throw new Error('Invalid order response: missing order ID');
      }

      setOrderResponse(result);

      // Save cart snapshot when order is created
      setCartSnapshot(JSON.parse(JSON.stringify(cartItems))); // Deep copy
      setHasCartChanged(false); // Reset change flag

      // Order created successfully
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[CheckoutContext] Error creating order:', err);
      Sentry.captureException(err, {
        tags: {
          area: 'checkout',
          operation: 'createOrder',
          ...(err.cloverCode && { clover_error: err.cloverCode }),
        },
        extra: {
          customerEmail: customerInfo?.email,
          lineItemCount: cartItems.length,
          pickupTime,
        },
      });
      const errorMessage =
        err.message || 'An unexpected error occurred. Please try again later.';
      setError(errorMessage);

      // The server refused the pickup time (#547). Nothing on step 1 renders
      // `error`, so say it in a toast; the slot grid keeps re-reading the
      // clock, and the customer picks again with cart and contact info intact.
      if (typeof err.code === 'string' && err.code.startsWith('PICKUP_')) {
        showError(errorMessage, { title: 'Pickup time unavailable' });
      }

      // Error occurred during order creation
    } finally {
      setIsSubmitting(false);
    }
  }, [
    customerInfo,
    cartItems,
    getCloverOrderData,
    pickupTime,
    tipAmount,
    orderNotes,
    menuData,
    removeCartItem,
    openModal,
    showError,
    showWarning,
  ]);

  const handlePayOrder = async (cardToken, cardholderName) => {
    if (!orderResponse?.order?.id) {
      setError('Your order must be created first. Please try again.');
      return;
    }

    if (!cardToken) {
      setError(
        'Payment information is missing. Please complete the payment form.',
      );
      return;
    }

    // Never pay an order twice, nor start a second payment while one is in
    // flight; a retry after a decline is fine (the order is not marked paid).
    const payingOrderId = orderResponse.order.id;
    if (
      paidOrderIdsRef.current.has(payingOrderId) ||
      payingOrderIdRef.current === payingOrderId
    ) {
      Sentry.addBreadcrumb({
        category: 'checkout',
        level: 'warning',
        message: 'Duplicate payment attempt ignored',
        data: { orderId: payingOrderId },
      });
      return;
    }
    payingOrderIdRef.current = payingOrderId;

    setIsProcessingPayment(true);
    setError(null);
    setPaymentResponse(null);

    try {
      const response = await fetch('/.netlify/functions/payOrder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': generateRequestId(),
        },
        body: JSON.stringify({
          orderId: orderResponse.order.id,
          cardToken,
          cardholderName:
            cardholderName ||
            `${customerInfo.firstName} ${customerInfo.lastName}`.trim(),
          customerInfo,
          tipAmount, // Pass tip to payment API for proper accounting
          pickupTime, // #427: lets payOrder defer the kitchen fire for scheduled orders
        }),
      });

      if (!response.ok) {
        const errorData = await readErrorBody(response, 'payOrder');

        throw buildPayOrderError(response.status, errorData);
      }

      const result = await response.json();

      if (!result.payment) {
        throw new Error('Invalid payment response');
      }

      paidOrderIdsRef.current.add(payingOrderId);
      setPaymentResponse(result);
      clearCart();

      // Clear checkout state from localStorage after successful payment
      clearCheckoutState();
      // ...but keep the receipt, so a reload still shows the confirmation
      // (#584). Built from the LIVE tip, the value payOrder just charged.
      saveReceipt(
        buildReceiptSnapshot({
          orderResponse,
          paymentResult: result,
          pickupTime,
          tipAmount,
          orderNotes,
        }),
      );

      // Payment successful
    } catch (caught) {
      // A dropped connection is explained in plain words (#632).
      const err = describeNetworkFailure(caught);
      const errorMessage =
        err.message ||
        'An error occurred while processing your payment. Please try again.';
      setError(errorMessage);

      // The server refused the pickup time before charging (#547): the slot
      // the customer chose on step 1 is no longer bookable. Treat it like
      // "back to contact info" — drop the unpaid Clover order and return to
      // step 1 with cart, contact info, tip and pickup choice intact — rather
      // than a payment failure that invites a retry on the same slot.
      if (err.isPickupRejection) {
        showError(errorMessage, { title: 'Pickup time unavailable' });
        try {
          await deleteOrder(orderResponse.order.id);
        } catch (deleteErr) {
          // cleanup-abandoned-orders sweeps unpaid orders; not worth failing over
          Sentry.captureException(deleteErr, {
            tags: { area: 'checkout', operation: 'pickupRejectionCleanup' },
          });
        }
        resetCheckout(false, true);
      }

      // Error occurred during payment processing
      throw err;
    } finally {
      setIsProcessingPayment(false);
      payingOrderIdRef.current = null;
    }
  };

  /**
   * Recreates an order by deleting the existing order and resetting order state.
   * Preserves customer info, pickup time, tip, and order notes.
   * Used when user modifies cart after reaching payment screen.
   *
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const recreateOrder = useCallback(async () => {
    // Validate we have an order to delete
    if (!orderResponse?.order?.id) {
      const error = 'No order to recreate';
      Sentry.captureMessage(error, {
        level: 'warning',
        tags: {
          component: 'CheckoutContext',
          action: 'recreateOrder',
        },
      });
      return { success: false, error };
    }

    const orderId = orderResponse.order.id;

    try {
      // Log recreation attempt
      Sentry.addBreadcrumb({
        message: 'Attempting to recreate order',
        category: 'checkout',
        level: 'info',
        data: {
          orderId,
          hasCartChanged,
          cartItemsCount: cartItems?.length || 0,
        },
      });

      // Delete existing order from Clover
      await deleteOrder(orderId);

      // Clear order-related state but preserve customer info
      setOrderResponse(null);
      setCartSnapshot(null);
      setHasCartChanged(false);
      setError(null);

      // Immediately update localStorage to clear orderResponse
      // This ensures when user navigates to /checkout, they see Step 1
      saveCheckoutState({
        customerInfo,
        pickupTime,
        tipAmount,
        tipPercentage,
        orderNotes,
        orderResponse: null, // Explicitly clear order
        paymentResponse: null,
      });

      // Log success
      Sentry.addBreadcrumb({
        message: 'Order recreated successfully',
        category: 'checkout',
        level: 'info',
        data: {
          orderId,
        },
      });

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete existing order';

      // Log error to Sentry
      Sentry.captureException(err, {
        tags: {
          component: 'CheckoutContext',
          action: 'recreateOrder',
          orderId,
        },
        extra: {
          orderId,
          hasCartChanged,
          cartItemsCount: cartItems?.length || 0,
          errorMessage,
        },
      });

      // Return error but don't throw - allow graceful degradation
      return { success: false, error: errorMessage };
    }
  }, [
    orderResponse,
    hasCartChanged,
    cartItems,
    customerInfo,
    pickupTime,
    tipAmount,
    tipPercentage,
    orderNotes,
  ]);

  /**
   * Reset checkout state but preserve customer info for better UX
   * Customer info persists in localStorage across orders
   * @param {boolean} _clearCustomer - DEPRECATED - customer info always persists now
   * @param {boolean} preserveTip - If true, preserves tip percentage and amount (for going back to edit)
   */
  const resetCheckout = useCallback(
    (_clearCustomer = false, preserveTip = false) => {
      setIsSubmitting(false);
      setOrderResponse(null);
      setError(null);
      setPaymentResponse(null);
      setIsProcessingPayment(false);
      setValidationErrors({});

      // Customer info is NOT cleared - it persists in localStorage across orders
      // This provides better UX for returning customers

      // Tip percentage is cleared UNLESS we're going back to edit (preserveTip = true)
      if (!preserveTip) {
        setTipPercentage(null);
        setTipAmount(0);
      }

      // Going back to edit keeps the pickup choice; a finished or cancelled
      // order clears it so the next checkout asks the question again (#484).
      if (!preserveTip) setPickupTime(null);
      setOrderNotes('');
      setCartSnapshot(null);
      setHasCartChanged(false);

      // If preserving tip, save the preserved state to localStorage
      // Otherwise, clear checkout state completely
      if (preserveTip) {
        // Use current values from state (not the ones being set above)
        saveCheckoutState({
          customerInfo,
          pickupTime,
          tipAmount, // Current tip amount
          tipPercentage, // Current tip percentage
          orderNotes: '',
          orderResponse: null,
          paymentResponse: null,
          cartSnapshot: null,
        });
      } else {
        clearCheckoutState();
        // Return to Menu, or a cancelled order: the receipt is done with too.
        clearReceipt();
      }
    },
    // Setters are stable; only the values echoed into localStorage on
    // preserveTip matter. Stable identity keeps effects keyed on it from
    // re-running every render (#554).
    [customerInfo, pickupTime, tipAmount, tipPercentage],
  );

  const value = {
    // State
    isSubmitting,
    orderResponse,
    error,
    paymentResponse,
    isProcessingPayment,
    validationErrors,
    customerInfo,
    cartItems, // Expose cart items from CartContext

    // New checkout fields
    pickupTime,
    tipAmount,
    tipPercentage,
    orderNotes,

    // Cart change detection
    hasCartChanged,
    cartSnapshot,

    // Functions
    updateCustomerInfo,
    validateAndSubmitOrder,
    handlePayOrder,
    recreateOrder,
    resetCheckout,
    setError,
    setPickupTime,
    setTipAmount,
    setTipPercentage,
    setOrderNotes,
  };

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};
